import { useEffect, useMemo, useRef, useState } from "react";

import { Constants, useMeeting, usePubSub, useMediaDevice, useWhiteboard, createCameraVideoTrack, createMicrophoneAudioTrack } from "../config/sdkConfig";

import { useMeetingAppContext } from "../MeetingAppContextDef";
import { useSiteContext } from "../context/SiteContext";
import {
  VirtualBackgroundProcessor,
  VideoSDKNoiseSuppressor,
} from "@videosdk.live/videosdk-media-processor-web";
import useIsRecording from "./useIsRecording";
import useIsMobile from "./useIsMobile";
import useWindowSize from "./useWindowSize";
import * as meetingServices from "../services/meetingServices";
import pollServices from "../services/pollServices";
import { startStreaming, stopStreaming } from "../services/streamingService";
import { toast } from "react-toastify";
import { useMeetingState } from "../context/MeetingStateContext";
import requests from "../services/httpServices";

const normalizePollForClient = (poll) => {
  if (!poll || typeof poll !== "object") {
    return null;
  }

  const votes = Array.isArray(poll.votes)
    ? poll.votes
      .map((vote) => ({
        ...vote,
        optionIndices: Array.isArray(vote?.optionIndices)
          ? vote.optionIndices
          : [],
      }))
      .filter((vote) => vote.participantId)
    : [];

  const skipped = Array.isArray(poll.skipped)
    ? poll.skipped
      .map((entry) => ({
        participantId: entry?.participantId,
        participantName:
          entry?.participantName ||
          entry?.name ||
          entry?.displayName ||
          "Anonymous",
        timestamp: entry?.timestamp || Date.now(),
      }))
      .filter((entry) => entry.participantId)
    : [];

  const options = (poll.options || []).map((option, index) => {
    const resolvedIndex =
      typeof option === "object" &&
        option !== null &&
        typeof option.index === "number"
        ? option.index
        : index;

    if (typeof option === "string") {
      return {
        text: option,
        index: resolvedIndex,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    if (option && typeof option === "object") {
      const resolvedText =
        option.text ||
        option.label ||
        option.value ||
        `Option ${resolvedIndex + 1}`;

      return {
        ...option,
        index: resolvedIndex,
        text: resolvedText,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    const value = option ?? `Option ${resolvedIndex + 1}`;
    return {
      text: String(value),
      index: resolvedIndex,
      votes: votes.filter((vote) => vote.optionIndices?.includes(resolvedIndex))
        .length,
    };
  });

  const sanitizedPoll = {
    ...poll,
    options,
    votes,
    skipped,
    totalVotes: votes.length,
    skippedCount: skipped.length,
    responseCount: votes.length + skipped.length,
  };

  if (!sanitizedPoll.id) {
    sanitizedPoll.id = poll.pollId || poll._id || `${Date.now()}`;
  }

  sanitizedPoll.lastUpdated = Date.now();

  return sanitizedPoll;
};

const applyVoteToPoll = (poll, vote) => {
  if (!poll || !vote) return poll;

  const normalizedVote = {
    ...vote,
    optionIndices: Array.isArray(vote.optionIndices) ? vote.optionIndices : [],
  };

  const existingVotes = Array.isArray(poll.votes) ? [...poll.votes] : [];

  const existingIndex = existingVotes.findIndex(
    (entry) => entry.participantId === normalizedVote.participantId
  );

  if (existingIndex >= 0) {
    existingVotes[existingIndex] = normalizedVote;
  } else {
    existingVotes.push(normalizedVote);
  }

  const filteredSkipped = Array.isArray(poll.skipped)
    ? poll.skipped.filter(
      (entry) => entry.participantId !== normalizedVote.participantId
    )
    : [];

  return normalizePollForClient({
    ...poll,
    votes: existingVotes,
    skipped: filteredSkipped,
  });
};

const applySkipToPoll = (poll, skipEntry) => {
  if (!poll || !skipEntry) return poll;

  const normalizedSkip = {
    participantId: skipEntry.participantId,
    participantName:
      skipEntry.participantName ||
      skipEntry.name ||
      skipEntry.displayName ||
      "Anonymous",
    timestamp: skipEntry.timestamp || Date.now(),
  };

  if (!normalizedSkip.participantId) {
    return poll;
  }

  const filteredVotes = Array.isArray(poll.votes)
    ? poll.votes.filter(
      (entry) => entry.participantId !== normalizedSkip.participantId
    )
    : [];

  const skippedEntries = Array.isArray(poll.skipped) ? [...poll.skipped] : [];

  const existingIndex = skippedEntries.findIndex(
    (entry) => entry.participantId === normalizedSkip.participantId
  );

  if (existingIndex >= 0) {
    skippedEntries[existingIndex] = normalizedSkip;
  } else {
    skippedEntries.push(normalizedSkip);
  }

  return normalizePollForClient({
    ...poll,
    votes: filteredVotes,
    skipped: skippedEntries,
  });
};

export const useTopBar = ({
  setIsMeetingLeft,
  setCustomVideoStream,
  micOn,
  setMicOn,
  webcamOn,
  setWebcamOn,
  customAudioStream,
  setCustomAudioStream,
  customVideoStream,
  startTranscription,
  startTranslation,
  stopTranslation,
  stopTranscription,
  changeTranslationLanguage,
  publishMeetingSettings,
  admitAllWaitingParticipants,
  showRewardsPanel,
  setShowRewardsPanel,
  handleToggleAICaptions,
  aiCaptionsEnabled,
}) => {
  const { startWhiteboard } = useWhiteboard();
  const {
    meetingRole,
    user,
    isAuthenticated,
    meetingSettings,
    setShowElementSection,
    showElementSection,
    selectedLayout,
    setSelectedLayout,
    hideSelfView,
    setHideSelfView,
    showNonVideoParticipants,
    setShowNonVideoParticipants,
    videoResolution,
    handleVideoResolution,
    setMeetingSession,
    translationStatus,
    showPollsModal,
    setShowPollsModal,
    polls,
    setPolls,
    selectedPoll,
    setSelectedPoll,
    loadingPolls,
    setLoadingPolls,
    showPollResultsModal,
    setShowPollResultsModal,
    pollResults,
    setPollResults,
    setShowPollResults,
    meetingSession,
    setMeetingSettings,
    customMeetingName,
  } = useSiteContext();

  const {
    selectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    setSideBarMode,
    selectedWebcam,
    setUnreadChat,
    unreadChat,
    recentReactions,
    selectedLanguage,
    setSelectedLanguage,
    setTranslationEnabled,
    translationEnabled,
    setTranscriptionEnabled,
    transcriptionEnabled,
    virtualBackgroundOn,
    setVirtualBackgroundOn,
    blurEnabled,
    setBlurEnabled,
    blurStrength,
    setBlurStrength,
    selectedBackground,
    setSelectedBackground,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    showRecordingConsentModal,
    setShowRecordingConsentModal,
    participantApproveFunc,
    setRecordingStartedById,
  } = useMeetingAppContext();

  const { meetingStartedAt, hostControls, emitForceMuteAll, emitGrantUnmuteAll, emitElementAdd, privacy: privacyMode } = useMeetingState();
  const [privacyNoticeOpen, setPrivacyNoticeOpenInternal] = useState(false);
  const setPrivacyNoticeOpen = (v) => setPrivacyNoticeOpenInternal(!!v);

  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const isMobileLike =
    (windowSize?.height || 0) > 0 && windowSize.height < 500 ? true : isMobile;

  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;

  const isMobileLandscape = isMobileLike && isLandscapeOrientation;

  const { getMicrophones, getPlaybackDevices, getCameras } = useMediaDevice();
  const videoProcessorRef = useRef(null);
  if (!videoProcessorRef.current) {
    videoProcessorRef.current = new VirtualBackgroundProcessor();
  }
  const videoProcessor = videoProcessorRef.current;
  const noiseProcessorRef = useRef(null);
  const mMeeting = useMeeting();
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const localMicOn = mMeeting?.localMicOn;
  const changeMic = mMeeting?.changeMic;

  const localWebcamOn = mMeeting?.localWebcamOn;
  const changeWebcam = mMeeting?.changeWebcam;

  const [showMore, setShowMore] = useState(false);
  const [showSetting, setShowSetting] = useState(false);
  const [showVirtualBackgroundModal, setShowVirtualBackgroundModal] =
    useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [webcams, setWebcams] = useState([]);
  const [showReactions, setShowReactions] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showCaptionsDropdown, setShowCaptionsDropdown] = useState(false);
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [showSpeakerDropdown, setShowSpeakerDropdown] = useState(false);
  const [showVideoDropdown, setShowVideoDropdown] = useState(false);
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const [showCaptionTranslationPanel, setShowCaptionTranslationPanel] =
    useState(false);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  const [localMicState, setLocalMicState] = useState(localMicOn);
  const [localWebcamState, setLocalWebcamState] = useState(localWebcamOn);

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpIssueText, setHelpIssueText] = useState("");
  const [showProfileConfirmModal, setShowProfileConfirmModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Live streaming states
  const [showLiveStreamModal, setShowLiveStreamModal] = useState(false);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [liveStreamPlatforms, setLiveStreamPlatforms] = useState({
    facebook: false,
    youtube: false,
    linkedin: false,
  });
  const [streamingCredentials, setStreamingCredentials] = useState({
    facebook: {
      url: "",
      key: "",
    },
    youtube: {
      url: "",
      backupUrl: "",
      key: "",
    },
    linkedin: {
      url: "",
      key: "",
    },
  });
  const [streamingStartTime, setStreamingStartTime] = useState(null);
  const [streamingElapsedMs, setStreamingElapsedMs] = useState(0);

  // Pre-populate streaming credentials from meetingSettings (saved during scheduling)
  useEffect(() => {
    if (meetingSettings) {
      const hasYouTube = meetingSettings.streamYouTubeUrl || meetingSettings.streamYouTubeKey;
      const hasFacebook = meetingSettings.streamFacebookUrl || meetingSettings.streamFacebookKey;
      if (hasYouTube || hasFacebook) {
        console.log('[RTMP] Pre-populating streaming credentials from meetingSettings');
        setStreamingCredentials(prev => ({
          ...prev,
          youtube: {
            ...prev.youtube,
            url: meetingSettings.streamYouTubeUrl || prev.youtube.url || 'rtmp://a.rtmp.youtube.com/live2',
            key: meetingSettings.streamYouTubeKey || prev.youtube.key,
          },
          facebook: {
            ...prev.facebook,
            url: meetingSettings.streamFacebookUrl || prev.facebook.url || 'rtmps://live-api-s.facebook.com:443/rtmp/',
            key: meetingSettings.streamFacebookKey || prev.facebook.key,
          },
        }));
        if (hasYouTube) {
          setLiveStreamPlatforms(prev => ({ ...prev, youtube: true }));
        }
        if (hasFacebook) {
          setLiveStreamPlatforms(prev => ({ ...prev, facebook: true }));
        }
      }
    }
  }, [meetingSettings?.streamYouTubeUrl, meetingSettings?.streamYouTubeKey, meetingSettings?.streamFacebookUrl, meetingSettings?.streamFacebookKey]);

  const [showAudioInputDropdown, setShowAudioInputDropdown] = useState(false);
  const [showVideoSourceDropdown, setShowVideoSourceDropdown] = useState(false);
  // blurEnabled, blurStrength, selectedBackground now come from MeetingAppContext
  const [showStartRecordingModal, setShowStartRecordingModal] = useState(false);
  const [recordingStartAt, setRecordingStartAt] = useState(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  // Loom-style 3-2-1 countdown shown right after pressing Record. Decrements
  // once a second, then hands off to recordingWaitingForStart while we wait
  // for the worker to confirm ffmpeg has actually started capturing.
  const [recordingCountdown, setRecordingCountdown] = useState(null);
  const recordingCountdownTimerRef = useRef(null);
  // Held true after the countdown until the backend reports
  // captureStartedAt (= the moment ffmpeg started writing frames). Without
  // this hold, RECORDING_STARTED fires when the API queues the worker, not
  // when capture is live — so users would start speaking 6-8 seconds early
  // and lose the start of their recording.
  const [recordingWaitingForStart, setRecordingWaitingForStart] = useState(false);
  const recordingWaitingTimeoutRef = useRef(null);
  const recordingWaitingPollRef = useRef(null);

  const [participantUnmutePermissions, setParticipantUnmutePermissions] =
    useState(new Map());

  const activePollRef = useRef(null);
  const isRecording = useIsRecording();
  const isRecordingRef = useRef(isRecording);

  const resolvedSessionId =
    meetingSession == null
      ? "Not available"
      : typeof meetingSession === "string"
      ? meetingSession
      : meetingSession.sessionId ||
        meetingSession.id ||
        meetingSession._id ||
        JSON.stringify(meetingSession);

  // PubSub hooks
  const { publish: publishRaiseHand } = usePubSub("RAISE_HAND");
  const { publish: publishReaction } = usePubSub("EMOJI_REACTION");

  const { publish: publishPoll } = usePubSub("POLL", {
    onMessageReceived: (data) => {
      // Ignore stale persisted messages — late joiners get state from Socket.IO snapshot
      if (data.isPersisted) return;
      const messageData = data.message || data;

      if (messageData?.type === "launch" && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
          setShowPollResultsModal(false);
          // Ensure host sees the PollResults panel when launching
          if (data.isLocal) {
            setShowPollResults(true);
          }
        }
      }
    },
  });

  const { publish: publishPollVoteChannel } = usePubSub("POLL_VOTE", {
    onMessageReceived: (data) => {
      if (data.isPersisted) return;
      const messageData = data.message || data;
      if (!messageData) return;

      const { type, pollId } = messageData;

      const resolveBasePoll = (id) => {
        if (!id) return null;

        if (activePollRef.current && activePollRef.current.id === id) {
          return activePollRef.current;
        }

        if (pollResults && pollResults.id === id) {
          return pollResults;
        }

        return null;
      };

      const broadcastResultsIfNeeded = (poll) => {
        if (poll?.settings?.shareResultsLive) {
          try {
            publishPollVoteChannel({
              type: "results_update",
              pollId: poll.id,
              poll,
              timestamp: Date.now(),
            });
          } catch (error) {
            console.error("Error broadcasting poll results:", error);
          }
        }

        if (
          ["moderator", "co-host"].includes(meetingRole) &&
          poll?.settings?.showResultsAtEnd &&
          poll?.responseCount > 0
        ) {
          setShowPollResultsModal(true);
        }
      };

      if (type === "vote_submitted") {
        if (!pollId) {
          console.error("vote_submitted event missing pollId", messageData);
          return;
        }

        if (!["moderator", "co-host"].includes(meetingRole)) {
          return;
        }

        const incomingVote = messageData.vote || {
          participantId: messageData.participantId,
          participantName: messageData.participantName,
          optionIndices: messageData.optionIndices,
          timestamp: messageData.timestamp,
        };

        let pollForBroadcast = null;

        setPollResults((previousPoll) => {
          const basePoll = resolveBasePoll(pollId) || previousPoll;

          if (!basePoll) {
            console.warn("Poll state not found for vote_submitted", pollId);
            return previousPoll;
          }

          const updatedPoll = applyVoteToPoll(basePoll, incomingVote);
          pollForBroadcast = updatedPoll;
          activePollRef.current = updatedPoll;
          return updatedPoll;
        });

        broadcastResultsIfNeeded(pollForBroadcast);
        return;
      }

      if (type === "vote_skipped") {
        if (!pollId) {
          console.error("vote_skipped event missing pollId", messageData);
          return;
        }

        if (!["moderator", "co-host"].includes(meetingRole)) {
          return;
        }

        const incomingSkip = messageData.skip || {
          participantId: messageData.participantId,
          participantName: messageData.participantName,
          timestamp: messageData.timestamp,
        };

        let pollForBroadcast = null;

        setPollResults((previousPoll) => {
          const basePoll = resolveBasePoll(pollId) || previousPoll;

          if (!basePoll) {
            console.warn("Poll state not found for vote_skipped", pollId);
            return previousPoll;
          }

          const updatedPoll = applySkipToPoll(basePoll, incomingSkip);
          pollForBroadcast = updatedPoll;
          activePollRef.current = updatedPoll;
          return updatedPoll;
        });

        broadcastResultsIfNeeded(pollForBroadcast);
        return;
      }

      if (type === "results_update" && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
          if (
            ["moderator", "co-host"].includes(meetingRole) &&
            normalizedPoll.settings?.showResultsAtEnd &&
            normalizedPoll.responseCount > 0
          ) {
            setShowPollResultsModal(true);
          }
        }
        return;
      }

      if (!type && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
        }
      }
    },
  });

  const { publish: publishRecordingNotice } = usePubSub("RECORDING_NOTICE", {
    onMessageReceived: ({ message, senderId }) => {
      if (message?.type === "started") {
        // Track the recorder so the participants panel can show a red dot
        // next to their name. Set for everyone, including the sender (so a
        // late-mounted component can still derive the recorder identity).
        setRecordingStartedById?.(senderId);
        if (senderId != mMeeting?.localParticipant?.id) {
          if (!recordingStartAt) {
            setRecordingStartAt(message.at || Date.now());
          }
          if (!["moderator", "co-host"].includes(meetingRole)) {
            setShowRecordingConsentModal(true);
          }
        }
      } else if (message?.type === "stopped") {
        setRecordingStartedById?.(null);
      }
    },
  });

  usePubSub("PARTICIPANT_UNMUTE_PERMISSION", {
    onMessageReceived: (data) => {
      console.log("PARTICIPANT_UNMUTE_PERMISSION", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      if (data?.message?.participantId == localParticipantId) {
        setParticipantUnmutePermissions((prev) => {
          if (data?.message?.canUnmute) {
            setMeetingSettings((prev) => ({
              ...prev,
              guestCanUnmute: true,
            }));
          } else {
            setMeetingSettings((prev) => ({
              ...prev,
              guestCanUnmute: false,
            }));
          }
        });
      }
    },
  });

  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        mMeeting.recordingState ===
          Constants.recordingEvents.RECORDING_STARTING ||
        mMeeting.recordingState ===
          Constants.recordingEvents.RECORDING_STOPPING,
    }),
    [mMeeting.recordingState]
  );

  // Event handlers
  const handleHelpSubmit = async () => {
    if (!helpIssueText.trim()) {
      toast.error("Please share your feedback before submitting.");
      return;
    }

    console.log("Feedback submitted:", {
      meetingId: mMeeting?.meetingId || "N/A",
      meetingSession: resolvedSessionId,
      issue: helpIssueText.trim(),
    });

    await meetingServices.sendFeedback({
      meetingId: mMeeting?.meetingId || "N/A",
      meetingSession: resolvedSessionId,
      issue: helpIssueText.trim(),
    });

    toast.success("Thanks! Your feedback has been captured.");
    setHelpIssueText("");
    setShowHelpModal(false);
  };

  const handleRecordingClick = () => {
    const isRecording = isRecordingRef.current;
    console.log("isRecording", mMeeting.recordingState);
    if (isRecording) {
      mMeeting.stopRecording();
      // Tell other clients to clear the "Recording" dot from the recorder's
      // row in the participants panel.
      // Persist with clear:true so late-joiner attribute is wiped — otherwise
      // a participant joining the next non-recording session would still see
      // the stale "started" attribute and falsely render the REC badge.
      try { publishRecordingNotice({ type: "stopped", clear: true }, { persist: true }); } catch (_) {}
    } else {
      // Privacy-mode gate at the source: block any start path (TopBar button,
      // MobileSwipeUpPanel, etc.) before the "Connecting Recorder" overlay or
      // backend call ever fires.
      if (privacyMode) {
        setPrivacyNoticeOpen(true);
        return;
      }
      // New multi-layout recording captures all layouts during the session
      // and lets viewers pick a layout at playback time, so the old
      // "choose layout before recording" picker is no longer needed.
      // Start immediately with a sensible default layout.
      handleConfirmStartRecording('speaker-top');
    }
  };

  const handleConfirmStartRecording = (recordingLayout) => {
    const at = Date.now();
    if (
      mMeeting.recordingState == Constants.recordingEvents.RECORDING_STARTED ||
      mMeeting.recordingState == Constants.recordingEvents.RECORDING_STARTING ||
      recordingWaitingForStart
    ) {
      return;
    }
    // Defense-in-depth: even auto-record paths (e.g. autoRecordWebinar) must
    // honor privacy mode. Surface the notice instead of starting.
    if (privacyMode) {
      setPrivacyNoticeOpen(true);
      return;
    }
    // Fire backend immediately and go straight into the "Connecting
    // recorder…" hold overlay — the previous 3-2-1 countdown was purely
    // cosmetic theater that added 3s of wall-clock for no protection
    // benefit (the hold-overlay below is what actually prevents the
    // data-loss issue where users started speaking before ffmpeg attached).
    mMeeting.startRecording(recordingLayout || 'speaker-top');
    // Credit the local user as the recorder authoritatively (don't rely on
    // pubsub echoing our own message back). This is the only place we can
    // safely set the local user — the RECORDING_STARTED state sync hits
    // every late-joiner too, so we can't infer "I started it" from state alone.
    if (mMeeting?.localParticipant?.id) {
      setRecordingStartedById?.(mMeeting.localParticipant.id);
    }
    try {
      // Persist so late joiners receive the "started" notice via the
      // channel attribute replay and can attribute the REC badge to the
      // right user. Without persist:true, anyone joining mid-recording
      // never learns who started it.
      publishRecordingNotice({ type: "started", at }, { persist: true });
    } catch (_) {}
    setShowStartRecordingModal(false);

    // Hold the overlay until the worker reports capture_started. Cleared
    // by the polling effect (poll captureStartedAt) or the 18s safety
    // timeout — see the useEffect above.
    setRecordingCountdown(null);
    setRecordingWaitingForStart(true);
  };

  const handleStopRecording = () => {
    mMeeting.stopRecording();
    try { publishRecordingNotice({ type: "stopped" }); } catch (_) {}
    stopTranscription();
  };

  const handlePauseRecording = async () => {
    try {
      await mMeeting.pauseRecording?.();
      toast.success('Recording paused');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to pause recording');
    }
  };

  const handleResumeRecording = async () => {
    try {
      await mMeeting.resumeRecording?.();
      toast.success('Recording resumed');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to resume recording');
    }
  };

  const formatDuration = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const handleStartLiveStream = async (platforms) => {
    try {
      console.log('[RTMP] handleStartLiveStream called with platforms:', JSON.stringify(platforms));
      console.log('[RTMP] Current streamingCredentials:', JSON.stringify({
        youtube: { url: streamingCredentials.youtube.url, hasKey: !!streamingCredentials.youtube.key },
        facebook: { url: streamingCredentials.facebook.url, hasKey: !!streamingCredentials.facebook.key },
        linkedin: { url: streamingCredentials.linkedin.url, hasKey: !!streamingCredentials.linkedin.key },
      }));

      const platformsList = [];

      if (platforms.facebook && streamingCredentials.facebook.url && streamingCredentials.facebook.key) {
        platformsList.push({
          name: "facebook",
          rtmpUrl: streamingCredentials.facebook.url,
          streamKey: streamingCredentials.facebook.key,
        });
      }

      if (platforms.youtube && streamingCredentials.youtube.url && streamingCredentials.youtube.key) {
        platformsList.push({
          name: "youtube",
          rtmpUrl: streamingCredentials.youtube.url,
          streamKey: streamingCredentials.youtube.key,
        });
      }

      if (platforms.linkedin && streamingCredentials.linkedin.url && streamingCredentials.linkedin.key) {
        platformsList.push({
          name: "linkedin",
          rtmpUrl: streamingCredentials.linkedin.url,
          streamKey: streamingCredentials.linkedin.key,
        });
      }

      if (platformsList.length === 0) {
        alert("Please select at least one platform with valid credentials to stream to.");
        return;
      }

      const channelName = mMeeting?.meetingId;
      if (!channelName) {
        alert("Meeting ID not available. Cannot start streaming.");
        return;
      }

      // Collect participant UIDs from the Agora client for RTMP layout
      const streamUids = [];
      const client = mMeeting?.client;
      if (client) {
        // Add local user UID
        if (client.uid) {
          streamUids.push(Number(client.uid));
        }
        // Add remote user UIDs (skip screen share UIDs which are uid+1, and STT bots 999000-999999)
        if (client.remoteUsers) {
          for (const user of client.remoteUsers) {
            const uid = Number(user.uid);
            if (uid >= 999000 && uid <= 999999) continue; // Skip STT bots
            streamUids.push(uid);
          }
        }
      }

      console.log("[RTMP] Starting streaming to:", platformsList.map(p => p.name), "with UIDs:", streamUids);
      const response = await startStreaming({ channelName, platforms: platformsList, streamUids });
      console.log("[RTMP] Streaming started:", response.data || response);

      setIsLiveStreaming(true);
      setLiveStreamPlatforms(platforms);
      setStreamingStartTime(Date.now());
      setShowLiveStreamModal(false);

      toast.success("Live streaming started successfully!");
    } catch (error) {
      console.error("Error starting live stream:", error);
      const msg = error?.response?.data?.error || error?.message || "Failed to start live stream";
      toast.error(msg);
    }
  };

  const handleStopLiveStream = async () => {
    try {
      const channelName = mMeeting?.meetingId;
      if (!channelName) {
        console.warn("[RTMP] No meeting ID available for stopping stream");
        return;
      }

      console.log("[RTMP] Stopping streaming for channel:", channelName);
      const response = await stopStreaming({ channelName });
      console.log("[RTMP] Streaming stopped:", response.data || response);

      setIsLiveStreaming(false);
      setLiveStreamPlatforms({
        facebook: false,
        youtube: false,
        linkedin: false,
      });
      setStreamingStartTime(null);
      setStreamingElapsedMs(0);

      toast.success("Live streaming stopped");
    } catch (error) {
      console.error("Error stopping live stream:", error);
      toast.error("Failed to stop live stream. Please try again.");
    }
  };

  const fetchPolls = async () => {
    setLoadingPolls(true);
    try {
      const response = await pollServices.getUserPolls();
      if (response) {
        if (response.success && response.data) {
          setPolls(Array.isArray(response.data) ? response.data : []);
        } else if (Array.isArray(response)) {
          setPolls(response);
        } else if (response.data && Array.isArray(response.data)) {
          setPolls(response.data);
        } else {
          setPolls([]);
        }
      } else {
        setPolls([]);
      }
    } catch (error) {
      console.error("Error fetching polls:", error);
      setPolls([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const handleLaunchPoll = () => {
    if (!selectedPoll) {
      toast.error("Please select a poll to launch.");
      return;
    }

    try {
      const pollPayload = normalizePollForClient({
        ...selectedPoll,
        votes: [],
        skipped: [],
        totalVotes: 0,
      });

      if (!pollPayload) {
        toast.error("Unable to launch poll. Poll data is invalid.");
        return;
      }

      setShowPollsModal(false);
      setSelectedPoll(null);
      setShowPollResultsModal(false);
      setPollResults(pollPayload);
      activePollRef.current = pollPayload;
      setShowPollResults(true);
      publishPoll({
        type: "launch",
        poll: pollPayload,
        timestamp: Date.now(),
      }, { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      emitElementAdd({
        id: pollPayload.id || pollPayload._id,
        type: 'poll',
        config: { poll: pollPayload },
        addedAt: Date.now(),
      });
    } catch (error) {
      console.error("Error launching poll:", error);
      toast.error("Failed to launch poll. Please try again.");
    }
  };

  const getMics = async () => {
    const mics = await getMicrophones();
    const speakers = await getPlaybackDevices();

    mics && mics?.length && setMics(mics);
    speakers && speakers?.length && setSpeakers(speakers);
  };

  const getWebcams = async () => {
    let webcams = await getCameras();
    webcams && webcams?.length && setWebcams(webcams);
  };

  function onDeviceChanged(devices) {
    getMics();
    const newSpeakerList = devices.devices.filter(
      (device) => device.kind === "audiooutput"
    );

    if (newSpeakerList.length > 0 && !selectedSpeaker?.id) {
      setSelectedSpeaker({
        id: newSpeakerList[0].deviceId,
        label: newSpeakerList[0].label,
      });
    }
  }

  const handleToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (document.documentElement?.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement?.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement?.msRequestFullscreen) {
        await document.documentElement.msRequestFullscreen();
      } else {
        throw new Error("Fullscreen API not available");
      }
    } catch (_) {
      document.body.classList.toggle("ios-pseudo-fullscreen");
    } finally {
      setIsFullscreen(
        !!document.fullscreenElement ||
          document.body.classList.contains("ios-pseudo-fullscreen")
      );
      setShowMore(false);
    }
  };

  const handleCopyRoomLink = () => {
    try {
      const href = window.location?.href || "";
      navigator.clipboard?.writeText(href).catch(() => {});
    } catch (_) {}
    toast.success("Meeting link copied to clipboard!");
    setShowMore(false);
  };

  const handleToggleTranscription = () => {
    // Sync with AI Captions (bottom bar CC) instead of old transcription
    if (handleToggleAICaptions) {
      handleToggleAICaptions();
    }
  };

  const handleToggleMic = async () => {
    if (localMicOn) {
      if (noiseProcessorRef.current) {
        try {
          if (noiseProcessorRef.current.stopProcessing) {
            await noiseProcessorRef.current.stopProcessing();
          }
        } catch (error) {
          console.log("Error stopping noise processor:", error);
        }
        noiseProcessorRef.current = null;
      }
      setLocalMicState(false);
      mMeeting.muteMic();
    } else {
      if (meetingRole !== "moderator" && !meetingSettings?.guestCanUnmute) {
        toast.error(
          "You are not allowed to unmute your mic controlled by host"
        );
        return;
      }

      if (isMobile) {
        setLocalMicState(true);
        mMeeting.unmuteMic();
        if (noiseProcessorRef.current) {
          try {
            if (noiseProcessorRef.current.stopProcessing) {
              await noiseProcessorRef.current.stopProcessing();
            }
          } catch (error) {
            console.log("Error stopping noise processor:", error);
          }
        }
        noiseProcessorRef.current = new VideoSDKNoiseSuppressor();

        const stream = await createMicrophoneAudioTrack({});
        const processedStream =
          await noiseProcessorRef.current.getNoiseSuppressedAudioStream(stream);

        setCustomAudioStream(processedStream);
      } else {
        setLocalMicState(true);
        mMeeting.unmuteMic();
      }
    }
  };

  const handleStartVirtualBackground = async (background) => {
    console.log("videoProcessor", videoProcessor);

    if (!videoProcessor.ready) {
      await videoProcessor.init();
    }

    if (!background) {
      videoProcessor.stop();

      const stream = await createCameraVideoTrack({});
      changeWebcam(stream);
      setVirtualBackgroundOn(false);
      setSelectedBackground(null);
      return;
    }

    const config = blurEnabled
      ? {
          type: "blur",
          blurStrength: blurStrength,
        }
      : {
          type: "image",
          imageUrl: background,
        };

    const stream = await createCameraVideoTrack({});
    const processedStream = await videoProcessor.start(stream, config);
    console.log("webcamOn", webcamOn);
    if (webcamOn) {
      changeWebcam(processedStream);
      setVirtualBackgroundOn(true);
      setSelectedBackground(background);
    }
  };

  const handleLeaveModal = () => {
    console.log(mMeeting, "mMeeting");
    setShowLeaveModal(true);
    setMeetingSession(null);
    localStorage.setItem("rejoinId", window.location.href);
  };

  const handleLeaveConfirm = async () => {
    // Guard: guests who never published (e.g. left from the recording-consent
    // modal before enabling camera/mic) may not have a localParticipant or
    // the disable* methods may throw. An unhandled throw here would abort the
    // whole function and skip mMeeting.leave(), leaving the user stuck.
    try { mMeeting?.localParticipant?.disableWebcam?.(); } catch (_) { /* ignore */ }
    try { mMeeting?.localParticipant?.disableMic?.(); } catch (_) { /* ignore */ }

    if (noiseProcessorRef.current) {
      try {
        if (noiseProcessorRef.current.stopProcessing) {
          noiseProcessorRef.current.stopProcessing();
        }
      } catch (error) {
        console.log("Error stopping noise processor on leave:", error);
      }
      noiseProcessorRef.current = null;
    }

    try {
      if (customVideoStream && customVideoStream.getTracks) {
        customVideoStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      if (customAudioStream && customAudioStream.getTracks) {
        customAudioStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      const videoElements = document.querySelectorAll("video");
      videoElements.forEach((videoElement) => {
        if (videoElement.srcObject instanceof MediaStream) {
          videoElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });

      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audioElement) => {
        if (audioElement.srcObject instanceof MediaStream) {
          audioElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });
    } catch (error) {
      console.error("Error stopping media tracks:", error);
    }

    if (["moderator", "host", "co-host"].includes(meetingRole)) {
      try {
        // Use cumulative participant names (captures everyone who joined, not just still-connected)
        const cumulativeMap = mMeeting?.meetingParticipantsRef?.current;
        const participantList = cumulativeMap && cumulativeMap.size > 0
          ? [...cumulativeMap.values()].map(p => typeof p === 'object' ? { id: p.name, name: p.name, role: p.role } : { id: p, name: p })
          : [...(mMeeting?.participants?.values?.() || [])].map(p => ({
              id: p.id,
              name: p.displayName || 'Guest',
              role: p.metaData?.meetingRole || 'attendee',
            }));
        const sessionData = {
          roomId: meetingSettings?.roomId,
          meetingId: mMeeting?.meetingId,
          participants: participantList,
          startedAt: meetingStartedAt || new Date().toISOString(),
          meetingName: customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        };
        console.log('[SaveSession] Saving meeting session:', sessionData.roomId, 'participants:', participantList.map(p => p.name));
        await meetingServices.saveMeetingSession(sessionData);
        console.log('[SaveSession] Session saved successfully');
      } catch (error) {
        console.error("[SaveSession] Error saving session:", error);
      }
    } else {
      console.log('[SaveSession] Skipping - role:', meetingRole);
    }

    // Clear the backend's cached whiteboard room so the next meeting session
    // in this room gets a fresh whiteboard (not leftover drawings).
    if (mMeeting?.meetingId) {
      requests.delete(`/agora/whiteboard/room/${mMeeting.meetingId}`).catch(() => {});
    }

    try {
      mMeeting.leave();
    } catch (err) {
      console.error('[Leave] Error leaving meeting:', err);
    }
    setShowRecordingConsentModal(false);
    console.log("meetingSettings?.exitUrl", meetingSettings?.exitUrl);
    if (meetingSettings?.exitUrl && meetingSettings.exitUrl != "false") {
      window.location.href = meetingSettings.exitUrl;
    } else {
      setIsMeetingLeft(true);
      setShowLeaveModal(false);
    }
  };

  const handleEndForAll = async () => {
    mMeeting.localParticipant.disableWebcam();
    mMeeting.localParticipant.disableMic();

    if (noiseProcessorRef.current) {
      try {
        if (noiseProcessorRef.current.stopProcessing) {
          noiseProcessorRef.current.stopProcessing();
        }
      } catch (error) {
        console.log("Error stopping noise processor on end:", error);
      }
      noiseProcessorRef.current = null;
    }

    try {
      if (customVideoStream && customVideoStream.getTracks) {
        customVideoStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      if (customAudioStream && customAudioStream.getTracks) {
        customAudioStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      const videoElements = document.querySelectorAll("video");
      videoElements.forEach((videoElement) => {
        if (videoElement.srcObject instanceof MediaStream) {
          videoElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });

      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audioElement) => {
        if (audioElement.srcObject instanceof MediaStream) {
          audioElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });
    } catch (error) {
      console.error("Error stopping media tracks:", error);
    }

    if (["moderator", "host", "co-host"].includes(meetingRole)) {
      try {
        const cumulativeMap = mMeeting?.meetingParticipantsRef?.current;
        const participantList = cumulativeMap && cumulativeMap.size > 0
          ? [...cumulativeMap.values()].map(p => typeof p === 'object' ? { id: p.name, name: p.name, role: p.role } : { id: p, name: p })
          : [...(mMeeting?.participants?.values?.() || [])].map(p => ({
              id: p.id,
              name: p.displayName || 'Guest',
              role: p.metaData?.meetingRole || 'attendee',
            }));
        const sessionData = {
          roomId: meetingSettings?.roomId,
          meetingId: mMeeting?.meetingId,
          participants: participantList,
          startedAt: meetingStartedAt || new Date().toISOString(),
          meetingName: customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        };
        console.log('[SaveSession] Saving (end for all):', sessionData.roomId, 'participants:', participantList.map(p => p.name));
        await meetingServices.saveMeetingSession(sessionData);
        console.log('[SaveSession] End session saved successfully');
      } catch (error) {
        console.error("[SaveSession] Error saving end session:", error);
      }
    } else {
      console.log('[SaveSession] Skipping end - role:', meetingRole);
    }

    // Force-end on Agora side (clear kicking rules + waiting room) before local end
    const endMeetingId = mMeeting?.meetingId;
    if (endMeetingId) {
      try {
        await requests.post(`/agora/force-end-meeting/${endMeetingId}`);
      } catch (err) {
        console.error('[useTopBar] Failed to force-end meeting:', err);
      }
    }

    mMeeting.end();
    if (meetingSettings?.exitUrl && meetingSettings.exitUrl != "false") {
      window.location.href = meetingSettings.exitUrl;
    } else {
      setIsMeetingLeft(true);
      setShowLeaveModal(false);
    }
  };

  const handleLeaveCancel = () => {
    setShowLeaveModal(false);
  };

  const handleEmojiClick = ({ char, alt }) => {
    console.log("char", char);
    publishReaction(JSON.stringify({ char, alt }));
    setShowReactions(false);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > 50;
    const isDownSwipe = distance < -50;

    if (isUpSwipe) {
      setShowMobilePanel(true);
    } else if (isDownSwipe) {
      setShowMobilePanel(false);
    }
  };

  // Effects
  useEffect(() => {
    setLocalMicState(localMicOn);
  }, [localMicOn]);

  useEffect(() => {
    setLocalWebcamState(localWebcamOn);
  }, [localWebcamOn]);

  useEffect(() => {
    if (pollResults) {
      activePollRef.current = pollResults;
    }
  }, [pollResults]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Refs for latest handler/state values (avoids stale closures in event listener)
  const handleStartLiveStreamRef = useRef(handleStartLiveStream);
  const handleStopLiveStreamRef = useRef(handleStopLiveStream);
  const liveStreamPlatformsRef = useRef(liveStreamPlatforms);
  useEffect(() => { handleStartLiveStreamRef.current = handleStartLiveStream; }, [handleStartLiveStream]);
  useEffect(() => { handleStopLiveStreamRef.current = handleStopLiveStream; }, [handleStopLiveStream]);
  useEffect(() => { liveStreamPlatformsRef.current = liveStreamPlatforms; }, [liveStreamPlatforms]);

  // Listen for Go Live / Stop Live actions from StudioBottomControls
  useEffect(() => {
    const handleLivestreamAction = (e) => {
      const { action } = e.detail || {};
      console.log('[RTMP] Livestream action received:', action);

      if (action === 'stop') {
        handleStopLiveStreamRef.current();
      } else if (action === 'directStart') {
        // Directly start streaming with pre-populated credentials
        console.log('[RTMP] Direct start - using pre-populated credentials');
        handleStartLiveStreamRef.current(liveStreamPlatformsRef.current);
      } else {
        // openModal — show the ToolsDropdown with live stream config
        setShowToolsDropdown(true);
        setShowLiveStreamModal(true);
      }
    };
    window.addEventListener('rlink-livestream-action', handleLivestreamAction);
    return () => window.removeEventListener('rlink-livestream-action', handleLivestreamAction);
  }, []);

  useEffect(() => {
    let intervalId;
    if (isLiveStreaming && streamingStartTime) {
      intervalId = setInterval(() => {
        setStreamingElapsedMs(Date.now() - streamingStartTime);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLiveStreaming, streamingStartTime]);

  useEffect(() => {
    if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STARTED
    ) {
      // Don't start the timer until ffmpeg is genuinely capturing — the hold
      // overlay's polling effect sets recordingStartAt from the actual
      // captureStartedAt timestamp. Without this gate, the top-bar timer
      // would tick from the moment the API queues the worker (RECORDING_
      // STARTED), so by the time the overlay clears (~6s later) the user
      // sees "0:06" already and assumes they lost the start.
      if (!recordingStartAt && !recordingWaitingForStart) {
        setRecordingStartAt(Date.now());
        setRecordingElapsedMs(0);
      }
      // NOTE: do NOT fall back to localParticipant.id here. Late joiners
      // and non-recording remote participants ALSO see RECORDING_STARTED
      // (it gets synced via the join-time recording-status check). If we
      // credit the local user when they haven't actually pressed Record,
      // every remote participant ends up showing the red REC badge on
      // their own tile (bug reported with Chris/Krishan screenshots).
      // The recorder identity is set authoritatively by:
      //   1. handleConfirmStartRecording — sets it for the local clicker
      //   2. RECORDING_NOTICE pubsub onMessageReceived — sets it for everyone else
    } else if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      setRecordingStartAt(null);
      setRecordingElapsedMs(0);
      setRecordingStartedById?.(null);
    }
  }, [mMeeting.recordingState, recordingWaitingForStart]);

  useEffect(() => {
    let intervalId;
    if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STARTED &&
      recordingStartAt
    ) {
      intervalId = setInterval(() => {
        setRecordingElapsedMs(Date.now() - recordingStartAt);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mMeeting.recordingState, recordingStartAt]);

  // Hold-overlay clear logic. Polls /recording/:id/processing-status every
  // 1.5s for `captureStartedAt` (the timestamp the worker wrote when ffmpeg
  // started writing frames). When present, the recorder is genuinely live
  // and the user can speak. Safety timeout 18s in case the worker never
  // reports — at least we don't trap the user forever.
  useEffect(() => {
    if (!recordingWaitingForStart) return;

    const clearOverlay = () => setRecordingWaitingForStart(false);

    // Safety timeout — don't hold the user hostage if the worker never reports.
    if (!recordingWaitingTimeoutRef.current) {
      recordingWaitingTimeoutRef.current = setTimeout(() => {
        recordingWaitingTimeoutRef.current = null;
        clearOverlay();
      }, 18000);
    }

    // Need the Mongo recordingId to poll. AgoraContext exposes it as
    // `activeRecordingId`. If it's not set yet (start API still pending),
    // wait — the next render will retry with the populated value.
    const recId = mMeeting?.activeRecordingId;
    if (!recId) return;

    let cancelled = false;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('token') : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const checkOnce = async () => {
      try {
        const res = await fetch(`${baseUrl}/recording/${recId}/processing-status`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.captureStartedAt) {
          // Anchor the top-bar timer to the actual ffmpeg start moment so
          // the elapsed counter matches what the recorded MP4 will contain.
          // Without this, the timer would show ~6 extra seconds (the gap
          // between RECORDING_STARTED and capture_started).
          const captureStartTs = new Date(data.captureStartedAt).getTime();
          if (!Number.isNaN(captureStartTs)) {
            setRecordingStartAt(captureStartTs);
            setRecordingElapsedMs(Math.max(0, Date.now() - captureStartTs));
          }
          clearOverlay();
        }
      } catch (_) { /* ignore network blips */ }
    };

    // Fire once immediately so we don't waste the first 1.5s, then poll.
    checkOnce();
    recordingWaitingPollRef.current = setInterval(checkOnce, 1500);

    return () => {
      cancelled = true;
      if (recordingWaitingPollRef.current) {
        clearInterval(recordingWaitingPollRef.current);
        recordingWaitingPollRef.current = null;
      }
    };
  }, [recordingWaitingForStart, mMeeting?.activeRecordingId]);

  // Cleanup all hold-overlay timers when waitingForStart flips off (either
  // via the poll, the safety timeout, or recording stopping mid-launch).
  useEffect(() => {
    if (recordingWaitingForStart) return;
    if (recordingWaitingTimeoutRef.current) {
      clearTimeout(recordingWaitingTimeoutRef.current);
      recordingWaitingTimeoutRef.current = null;
    }
  }, [recordingWaitingForStart]);

  // Unmount cleanup: kill any in-flight countdown / hold timers.
  useEffect(() => {
    return () => {
      if (recordingCountdownTimerRef.current) clearInterval(recordingCountdownTimerRef.current);
      if (recordingWaitingTimeoutRef.current) clearTimeout(recordingWaitingTimeoutRef.current);
      if (recordingWaitingPollRef.current) clearInterval(recordingWaitingPollRef.current);
    };
  }, []);

  useEffect(() => {
    if (showPollsModal && (meetingRole === "moderator" || meetingRole === "co-host")) {
      fetchPolls();
    }
  }, [showPollsModal]);

  useMediaDevice({
    onDeviceChanged,
  });

  // Skip device enumeration for webinar participants (triggers getUserMedia permission prompt)
  const isWebinarViewerForDevices = meetingSettings?.roomType === "Webinar" && !["moderator", "co-host", "host", "speaker"].includes(meetingRole);
  useEffect(() => {
    if (!isWebinarViewerForDevices) {
      getWebcams();
      getMics();
    }
  }, [isWebinarViewerForDevices]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement ||
          document.body.classList.contains("ios-pseudo-fullscreen")
      );
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("msfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("msfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    console.log("user ", user);
  }, [user]);

  useEffect(() => {
    console.log("blurEnabled or blurStrength changed");
    if (virtualBackgroundOn) {
      handleStartVirtualBackground(selectedBackground);
    }
  }, [blurEnabled, blurStrength]);

  useEffect(() => {
    return () => {
      if (noiseProcessorRef.current) {
        try {
          if (noiseProcessorRef.current.stopProcessing) {
            noiseProcessorRef.current.stopProcessing();
          }
        } catch (error) {
          console.log("Error stopping noise processor on unmount:", error);
        }
        noiseProcessorRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const triggerButton = event.target.closest(
        'button[aria-label="Mic"], button[aria-label="Speaker"], button[aria-label="Video"], button[aria-label="Layout"], button[aria-label="Tools"], button[aria-label="Captions"], button[aria-label="Settings"], button[aria-label="Reactions"]'
      );
      if (triggerButton) return;

      if (
        showToolsDropdown ||
        showCaptionsDropdown ||
        showMicDropdown ||
        showSpeakerDropdown ||
        showVideoDropdown ||
        showLayoutDropdown ||
        showMore ||
        showReactions ||
        showCaptionTranslationPanel ||
        showProfileDropdown
      ) {
        const toolsDropdown = document.querySelector("[data-tools-dropdown]");
        const captionsDropdown = document.querySelector(
          "[data-captions-dropdown]"
        );
        const micDropdown = document.querySelector("[data-mic-dropdown]");
        const videoDropdown = document.querySelector("[data-video-dropdown]");
        const layoutDropdown = document.querySelector("[data-layout-dropdown]");
        const moreDropdown = document.querySelector("[data-more-dropdown]");
        const reactionsDropdown = document.querySelector(
          "[data-reactions-dropdown]"
        );
        const captionTranslationPanel = document.querySelector(
          "[data-caption-translation-panel]"
        );

        if (
          showToolsDropdown &&
          toolsDropdown &&
          !toolsDropdown.contains(event.target)
        ) {
          setShowToolsDropdown(false);
        }

        if (
          showCaptionsDropdown &&
          captionsDropdown &&
          !captionsDropdown.contains(event.target) &&
          !event.target.closest("[data-ai-captions-button]")
        ) {
          setShowCaptionsDropdown(false);
        }

        if (
          showMicDropdown &&
          micDropdown &&
          !micDropdown.contains(event.target)
        ) {
          setShowMicDropdown(false);
        }

        if (
          showSpeakerDropdown &&
          micDropdown &&
          !micDropdown.contains(event.target)
        ) {
          setShowSpeakerDropdown(false);
        }

        if (
          showVideoDropdown &&
          videoDropdown &&
          !videoDropdown.contains(event.target)
        ) {
          setShowVideoDropdown(false);
        }

        if (
          showLayoutDropdown &&
          layoutDropdown &&
          !layoutDropdown.contains(event.target)
        ) {
          setShowLayoutDropdown(false);
        }

        if (showMore && moreDropdown && !moreDropdown.contains(event.target)) {
          setShowMore(false);
        }

        if (
          showReactions &&
          reactionsDropdown &&
          !reactionsDropdown.contains(event.target)
        ) {
          setShowReactions(false);
        }

        if (
          showCaptionTranslationPanel &&
          captionTranslationPanel &&
          !captionTranslationPanel.contains(event.target)
        ) {
          setShowCaptionTranslationPanel(false);
        }

        const profileDropdown = document.querySelector("[data-profile-dropdown]");
        if (
          showProfileDropdown &&
          profileDropdown &&
          !profileDropdown.contains(event.target) &&
          !event.target.closest("[data-profile-button]")
        ) {
          setShowProfileDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    showToolsDropdown,
    showCaptionsDropdown,
    showMicDropdown,
    showSpeakerDropdown,
    showVideoDropdown,
    showLayoutDropdown,
    showMore,
    showReactions,
    showCaptionTranslationPanel,
    showProfileDropdown,
  ]);

  return {
    // Context values
    meetingRole,
    user,
    isAuthenticated,
    meetingSettings,
    showElementSection,
    setShowElementSection,
    selectedLayout,
    setSelectedLayout,
    hideSelfView,
    setHideSelfView,
    showNonVideoParticipants,
    setShowNonVideoParticipants,
    videoResolution,
    handleVideoResolution,
    translationStatus,
    showPollsModal,
    setShowPollsModal,
    polls,
    selectedPoll,
    setSelectedPoll,
    loadingPolls,
    showPollResultsModal,
    setShowPollResultsModal,
    pollResults,
    meetingSession,
    setMeetingSettings,

    // Meeting app context
    selectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    setSideBarMode,
    selectedWebcam,
    setUnreadChat,
    unreadChat,
    recentReactions,
    selectedLanguage,
    setSelectedLanguage,
    setTranslationEnabled,
    translationEnabled,
    setTranscriptionEnabled,
    transcriptionEnabled,
    virtualBackgroundOn,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    showRecordingConsentModal,
    setShowRecordingConsentModal,
    participantApproveFunc,

    // Local state
    isMobile,
    isMobileLike,
    isMobileLandscape,
    mics,
    speakers,
    localMicOn,
    changeMic,
    localWebcamOn,
    changeWebcam,
    showMore,
    setShowMore,
    showSetting,
    setShowSetting,
    showVirtualBackgroundModal,
    setShowVirtualBackgroundModal,
    showLeaveModal,
    setShowLeaveModal,
    webcams,
    showReactions,
    setShowReactions,
    showToolsDropdown,
    setShowToolsDropdown,
    showCaptionsDropdown,
    setShowCaptionsDropdown,
    showMicDropdown,
    setShowMicDropdown,
    showSpeakerDropdown,
    setShowSpeakerDropdown,
    showVideoDropdown,
    setShowVideoDropdown,
    showLayoutDropdown,
    setShowLayoutDropdown,
    showMobilePanel,
    setShowMobilePanel,
    showCaptionTranslationPanel,
    setShowCaptionTranslationPanel,
    isFullscreen,
    setIsFullscreen,
    compactMode,
    setCompactMode,
    localMicState,
    setLocalMicState,
    localWebcamState,
    setLocalWebcamState,
    showHelpModal,
    setShowHelpModal,
    helpIssueText,
    setHelpIssueText,
    // Privacy mode (gates Record / Auto-record at the source)
    privacyMode,
    privacyNoticeOpen,
    setPrivacyNoticeOpen,
    showProfileConfirmModal,
    setShowProfileConfirmModal,
    showProfileDropdown,
    setShowProfileDropdown,

    // Live streaming
    showLiveStreamModal,
    setShowLiveStreamModal,
    isLiveStreaming,
    liveStreamPlatforms,
    setLiveStreamPlatforms,
    streamingCredentials,
    setStreamingCredentials,
    streamingStartTime,
    streamingElapsedMs,

    // Recording
    showAudioInputDropdown,
    setShowAudioInputDropdown,
    showVideoSourceDropdown,
    setShowVideoSourceDropdown,
    blurEnabled,
    setBlurEnabled,
    blurStrength,
    setBlurStrength,
    selectedBackground,
    showStartRecordingModal,
    setShowStartRecordingModal,
    recordingStartAt,
    recordingElapsedMs,
    recordingCountdown,
    recordingWaitingForStart,
    isRecording,
    handlePauseRecording,
    handleResumeRecording,
    isRecordingPaused: mMeeting.isRecordingPaused,

    // Meeting
    mMeeting,
    startWhiteboard,
    isRequestProcessing,
    resolvedSessionId,

    // PubSub
    publishRaiseHand,
    publishReaction,

    // Handlers
    handleHelpSubmit,
    handleRecordingClick,
    handleConfirmStartRecording,
    handleStopRecording,
    formatDuration,
    handleStartLiveStream,
    handleStopLiveStream,
    handleLaunchPoll,
    getMics,
    getWebcams,
    handleToggleFullscreen,
    handleCopyRoomLink,
    handleToggleTranscription,
    handleToggleMic,
    handleStartVirtualBackground,
    handleLeaveModal,
    handleLeaveConfirm,
    handleEndForAll,
    handleLeaveCancel,
    handleEmojiClick,
    onTouchStart,
    onTouchMove,
    onTouchEnd,

    // Props passthrough
    startTranscription,
    stopTranscription,
    startTranslation,
    stopTranslation,
    changeTranslationLanguage,
    publishMeetingSettings,
    admitAllWaitingParticipants,
    webcamOn,
    customVideoStream,
    customAudioStream,
    setIsMeetingLeft,

    // Rewards Panel
    showRewardsPanel,
    setShowRewardsPanel,

    // Meeting state (mute controls)
    hostControls,
    emitForceMuteAll,
    emitGrantUnmuteAll,

    // AI Captions
    aiCaptionsEnabled,
  };
};
